import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import FullScreenImage from "../components/Home/ShowcaseSection";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import CareerSkillsSection from "../components/Courses/Shared/CareerSkillsSection";
import CourseProgramCards from "../components/Home/CourseProgramCards";
import cmsApi from "../services/cmsApi";
import { getImageUrl, FALLBACK_IMAGE_SVG } from "../utils/getImageUrl";

function formatSlugToTitle(slug) {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const DynamicCoursePage = () => {
  const { slug } = useParams();
  const [components, setComponents] = useState({});
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [programsLoading, setProgramsLoading] = useState(true);
  const pageTitle = formatSlugToTitle(slug || "");

  useEffect(() => {
    if (!slug) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await cmsApi.getPageComponents(slug);
        if (result.success && result.components) {
          const map = {};
          result.components
            .filter((c) => c.isActive !== false)
            .forEach((c) => {
              map[c.componentName] = c;
            });
          setComponents(map);
        }
      } catch (err) {
        console.error("Error fetching course page data:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchPrograms = async () => {
      try {
        setProgramsLoading(true);
        const result = await cmsApi.getProgramsByDepartment(slug);
        if (result.success && result.programs && result.programs.length > 0) {
          const transformed = result.programs.map((program, index) => ({
            id: program.id || index + 1,
            image: getImageUrl(program.featuredImage) || FALLBACK_IMAGE_SVG,
            heading: program.name || "",
            introduction:
              program.shortDescription || program.description || "",
            level: program.level || null,
            buttonText: "LEARN MORE",
            route:
              program.slug ||
              program.name.toLowerCase().replace(/\s+/g, "-"),
          }));
          setPrograms(transformed);
        }
      } catch (err) {
        console.error("Error fetching programs:", err);
      } finally {
        setProgramsLoading(false);
      }
    };

    fetchData();
    fetchPrograms();
  }, [slug]);

  const header = components["header"];
  const careerSkills = components["career-skills"];
  const admissionReqs = components["admission-requirements"];

  if (loading && typeof window !== "undefined") {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-custom-pink border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading {pageTitle} page...</p>
          </div>
        </div>
        </div>
        <Footer />
        <FloatingSocialPanels />
      </div>
    );
  }

  const headerTitle = header?.title || pageTitle;
  const headerSubtitle = header?.subtitle || "";
  const headerContent = header?.content || "Innovate. Create. Transform.";
  const headerImage =
    header?.mediaUrl ||
    "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=2072&q=80";

  const skillsTitle = careerSkills?.title || "TECH-READY SKILLS? CHECK.";
  const skillsContent = careerSkills?.content || "";
  const skillsParagraphs = skillsContent
    ? skillsContent.split("\n\n").filter(Boolean)
    : [`Welcome to the ${pageTitle} department at JRIIT.`];

  const admissionTitle = admissionReqs?.title || "ADMISSION REQUIREMENTS";
  const admissionContent =
    admissionReqs?.content ||
    "The applicant should posses at least four (4) passes in non-religious subjects in the Certificate of Secondary Education Examination (CSEE) or should posses the National Vocational Award Level III (Trade Test Grade I) in a relevant field from a recognized institution with any Certificate of Secondary Education Examination (CSEE) OR Equivalent as approved by the ATC Governing Board.";

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">

      {/* Hero Header - uses the exact same shared component as other courses */}
      <CourseHeader
        title={headerTitle}
        highlight={headerSubtitle}
        subtitle={headerContent}
        backgroundImage={headerImage}
      />

      {/* Career Skills Section - uses exact same shared component */}
      <CareerSkillsSection
        title={skillsTitle}
        paragraphs={skillsParagraphs}
      />

      {/* Programs Section */}
      {!programsLoading && programs.length > 0 && (
        <div className="flex justify-center space-x-0">
          <CourseProgramCards
            programs={programs}
            basePath={`/courses/${slug}`}
          />
        </div>
      )}

      {/* Admission Requirements - same styled section */}
      <AdmissionSection title={admissionTitle} content={admissionContent} />

      <FullScreenImage />
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

const AdmissionSection = ({ title, content }) => {
  const [visibleElements, setVisibleElements] = useState({});
  const sectionRef = React.useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(
            () => setVisibleElements((prev) => ({ ...prev, title: true })),
            100
          );
          setTimeout(
            () => setVisibleElements((prev) => ({ ...prev, content: true })),
            400
          );
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 lg:py-32 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-80 h-80 bg-custom-pink/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-32 left-20 w-96 h-96 bg-gold/10 rounded-full blur-3xl animate-float-delayed" />
      </div>
      <div className="absolute inset-0 bg-net-pattern opacity-3" />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div
          className={`text-center mb-2 transform transition-all duration-1000 ${
            visibleElements.title
              ? "translate-y-0 opacity-100"
              : "translate-y-10 opacity-0"
          }`}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-custom-pink leading-tight">
            {title}
          </h2>
          <div className="w-32 h-1 bg-gold mx-auto rounded-full mt-4" />
        </div>

        <div
          className={`transform transition-all duration-1000 delay-300 ${
            visibleElements.content
              ? "translate-y-0 opacity-100"
              : "translate-y-10 opacity-0"
          }`}
        >
          <div className="bg-white/70 backdrop-blur-sm p-8 md:p-12 lg:p-16">
            <div className="text-left max-w-7xl mx-auto">
              <p className="text-base md:text-lg lg:text-xl text-black leading-loose tracking-wide mb-8 whitespace-pre-line">
                {content}
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-15px) translateX(10px); }
          66% { transform: translateY(10px) translateX(-10px); }
        }
        @keyframes floatDelayed {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(20px) translateX(-15px); }
          66% { transform: translateY(-10px) translateX(15px); }
        }
        .animate-float { animation: float 10s ease-in-out infinite; }
        .animate-float-delayed { animation: floatDelayed 12s ease-in-out infinite 3s; }
      `}</style>
    </section>
  );
};

export default DynamicCoursePage;
